import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from './icons';

/**
 * Glossary terms (common namespace `glossary.<term>`): plain-language explanations of the words lay landlords stumble
 * over. Keep this list in sync with packages/i18n/locales/de/common.json.
 */
export const GLOSSARY_TERMS = [
  'operatingCosts',
  'apportionable',
  'allocationKey',
  'keyArea',
  'keyPersons',
  'keyUnits',
  'keyConsumption',
  'keyShares',
  'billingPeriod',
  'prepayment',
  'balance',
  'deadline',
  'objectionPeriod',
  'subMeters',
  'heatingOrdinance',
  'co2',
  'vacancy',
  'rentTypes',
  'rentIncrease',
  'hoa',
  'areas',
  'tenantObjection',
  'tenantBalance',
  'tenantCo2',
  'flatRent',
  'emailDelivery',
  'betrkv',
  'co2Tiers',
  'householdServices',
  'prepaymentVsFlat',
  'delivery',
  'targetCharge',
] as const;
export type GlossaryTerm = (typeof GLOSSARY_TERMS)[number];

const GAP = 8;
const EDGE = 16;
const WIDTH = 360;
/** Below this width the explanation opens as a panel at the bottom of the screen (thumb reach, no clipping). */
const NARROW = 600;

/**
 * "Was heißt das?": a small round info button next to a term. Click, Enter or Space opens a non-modal popover with the
 * plain-language explanation; Escape, the close button, a click outside or tabbing out closes it and focus returns to
 * the button. Rendered in a portal so table cells or cards with overflow never clip it; on phones it sits at the bottom.
 */
export function HelpTip({
  term,
  label,
}: {
  term: GlossaryTerm;
  /** Visible name of the term if it differs from the glossary title. */ label?: string;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; narrow: boolean } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const id = useId();
  const title = t(`glossary.${term}.title`);
  const name = label ?? title;

  const place = useCallback(() => {
    const b = btn.current?.getBoundingClientRect();
    if (!b) return;
    const narrow = window.innerWidth < NARROW;
    const height = pop.current?.offsetHeight ?? 220;
    const width = Math.min(WIDTH, window.innerWidth - 2 * EDGE);
    const left = Math.max(EDGE, Math.min(b.left - 12, window.innerWidth - width - EDGE));
    const below = b.bottom + GAP;
    const top =
      below + height > window.innerHeight - EDGE && b.top - GAP - height > EDGE
        ? b.top - GAP - height
        : below;
    setPos({ top, left, narrow });
  }, []);
  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) btn.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open, place]);
  useEffect(() => {
    if (!open) return;
    pop.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    const onDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (!pop.current?.contains(target) && !btn.current?.contains(target)) close(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, close, place]);

  // tabbing past the last control (or before the first) leaves the explanation and lands back on the button
  const onPopKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const items = [...(pop.current?.querySelectorAll<HTMLElement>('button, a[href]') ?? [])];
    const first = items[0];
    const last = items[items.length - 1];
    if (
      (!e.shiftKey && document.activeElement === last) ||
      (e.shiftKey && document.activeElement === first)
    ) {
      e.preventDefault();
      close();
    }
  };

  return (
    <>
      <button
        ref={btn}
        type="button"
        className={`help-tip ${open ? 'open' : ''}`}
        aria-label={t('help.button', { term: name })}
        title={t('help.ask')}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-haspopup="dialog"
        onClick={() => (open ? close() : setOpen(true))}
      >
        <Icon name="info" size={16} strokeWidth={1.8} />
      </button>
      {open &&
        createPortal(
          <div
            ref={pop}
            id={id}
            role="dialog"
            aria-modal="false"
            aria-labelledby={`${id}-t`}
            aria-describedby={`${id}-d`}
            className={`help-pop ${pos?.narrow ? 'narrow' : ''}`}
            style={pos && !pos.narrow ? { top: pos.top, left: pos.left } : undefined}
            onKeyDown={onPopKey}
          >
            <p className="help-pop-eyebrow">{t('help.ask')}</p>
            <h2 className="help-pop-title" id={`${id}-t`}>
              {title}
            </h2>
            <p className="help-pop-text" id={`${id}-d`}>
              {t(`glossary.${term}.text`)}
            </p>
            <p className="help-pop-note">{t('help.note')}</p>
            <button type="button" className="btn secondary sm" data-autofocus onClick={() => close()}>
              {t('help.close')}
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}

/** A term with its explanation button right after it, kept on one line (labels, headers, table heads). */
export function Term({ term, children, label }: { term: GlossaryTerm; children: ReactNode; label?: string }) {
  return (
    <span className="term">
      {children}
      <HelpTip term={term} label={label ?? (typeof children === 'string' ? children : undefined)} />
    </span>
  );
}
